import * as i0 from '@angular/core';
import { InjectionToken, makeEnvironmentProviders, Injectable, inject, APP_INITIALIZER } from '@angular/core';
import { ReplaySubject, Observable, filter } from 'rxjs';
import { Location } from '@angular/common';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { map, tap } from 'rxjs/operators';
const ANALYTICS_PROVIDER = new InjectionToken('EVENT_PROVIDER');
function provideFuturaAnalytics(analyticsProvider) {
  return makeEnvironmentProviders([{
    provide: ANALYTICS_PROVIDER,
    useClass: analyticsProvider
  }]);
}
function makeAttributes(attributes) {
  if (typeof attributes === 'string') {
    return attributes;
  }
  if (attributes == null) {
    return undefined;
  }
  const attributesPair = Object.keys(attributes).map(key => `${key}: ${attributes[key]}`);
  if (!attributesPair?.length) {
    return undefined;
  }
  return attributesPair.join(', ');
}
let ScrollService = /*#__PURE__*/(() => {
  class ScrollService {
    constructor() {
      this.pageScrollStatus = new ReplaySubject(1);
    }
    set scroll(info) {
      this.pageScrollStatus.next(info);
    }
    pageScroll() {
      return this.pageScrollStatus.asObservable();
    }
    static {
      this.ɵfac = function ScrollService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ScrollService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: ScrollService,
        factory: ScrollService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return ScrollService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
function delayedReturn(result) {
  return new Observable(resolve => {
    setTimeout(() => {
      resolve.next(result);
      resolve.complete();
    });
  });
}
let UrlParamsService = /*#__PURE__*/(() => {
  class UrlParamsService {
    constructor() {
      this.activateRoute = inject(ActivatedRoute);
      this.router = inject(Router);
    }
    setFilter(name, filter) {
      const queryParams = {};
      queryParams[name] = filter;
      return this.setFilters(queryParams);
    }
    setFilters(filters) {
      this.router.navigate([], {
        relativeTo: this.activateRoute,
        queryParams: filters,
        queryParamsHandling: 'merge',
        preserveFragment: true,
        replaceUrl: true
      }).catch(console.error);
      return delayedReturn();
    }
    setFragment(fragment) {
      this.router.navigate([], {
        relativeTo: this.activateRoute,
        fragment,
        queryParamsHandling: 'merge',
        replaceUrl: true
      }).catch(console.error);
      return delayedReturn();
    }
    getFilter(name) {
      return this.activateRoute.queryParamMap.pipe(map(params => params.get(name)));
    }
    getSnapshotFilter(name) {
      return this.activateRoute.snapshot.queryParamMap.get(name);
    }
    getFragment() {
      return this.activateRoute.fragment;
    }
    getSnapshotFragment() {
      return this.activateRoute.snapshot.fragment;
    }
    static {
      this.ɵfac = function UrlParamsService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || UrlParamsService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: UrlParamsService,
        factory: UrlParamsService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return UrlParamsService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let HistoryService = /*#__PURE__*/(() => {
  class HistoryService {
    constructor() {
      this.location = inject(Location);
      this.router = inject(Router);
      this.urlParamsService = inject(UrlParamsService);
      this.siteHistory = [];
      this.backAdditionalParams = {};
      this.router.events.pipe(filter(event => event instanceof NavigationEnd), map(event => event), tap(event => this.siteHistory.push(event.urlAfterRedirects)), tap(() => {
        if (!this.backAdditionalParams) {
          return;
        }
        const params = this.backAdditionalParams;
        if (Object.keys(params).length) {
          this.urlParamsService.setFilters(params);
        }
        this.backAdditionalParams = null;
      })).subscribe();
    }
    backWithParams(defaultPage, backParams) {
      this.backAdditionalParams = backParams || null;
      if (typeof defaultPage === 'object' && 'page' in defaultPage) {
        this.back(defaultPage.page, defaultPage.extras);
      } else {
        this.back(defaultPage);
      }
    }
    back(defaultPage, extras) {
      this.siteHistory.pop();
      if (this.siteHistory.length > 0) {
        this.location.back();
      } else if (defaultPage) {
        if (!Array.isArray(defaultPage)) {
          defaultPage = [defaultPage];
        }
        this.router.navigate(defaultPage, extras).catch(console.error);
      } else {
        this.router.navigateByUrl('/').catch(console.error);
      }
    }
    static {
      this.ɵfac = function HistoryService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || HistoryService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: HistoryService,
        factory: HistoryService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return HistoryService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let AnchorService = /*#__PURE__*/(() => {
  class AnchorService {
    goToAnchor(anchor, behavior) {
      const element = document.getElementById(anchor);
      if (element) {
        element.scrollIntoView(behavior);
      }
    }
    static {
      this.ɵfac = function AnchorService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AnchorService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: AnchorService,
        factory: AnchorService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return AnchorService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
function initAnchor(route, anchorService, behavior) {
  return () => {
    route.fragment.pipe(filter(fragment => !!fragment), map(fragment => fragment)).subscribe(fragment => anchorService.goToAnchor(fragment, behavior));
  };
}
function anchorProvider(behavior) {
  return makeEnvironmentProviders([{
    provide: APP_INITIALIZER,
    useFactory: (route, anchorService) => initAnchor(route, anchorService, behavior),
    deps: [ActivatedRoute, AnchorService],
    multi: true
  }]);
}

/**
 * Generated bundle index. Do not edit.
 */

export { ANALYTICS_PROVIDER, AnchorService, HistoryService, ScrollService, UrlParamsService, anchorProvider, delayedReturn, makeAttributes, provideFuturaAnalytics };
